import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, ShieldCheck, ArrowLeft, Lock } from "lucide-react";
import { track } from "@/lib/analytics";
import { useSeo, SITE } from "@/hooks/useSeo";

const PAY_OPTIONS = [
  {
    id: "gutter-cleaning",
    title: "Gutter Cleaning",
    price: "$80",
    priceNote: "Rest-of-June rate · $100 after July 1",
    description:
      "Full hand-clean, downspouts flushed, all debris bagged and hauled off.",
    href: "https://buy.stripe.com/5kQfZi3tib3w4D98e22sM00",
    testId: "stripe-pay-cleaning",
    accent: "bg-[#5FBE1F]",
  },
  {
    id: "neighbor-special",
    title: "Neighbor Special",
    price: "$150",
    priceNote: "Two homes on the same block, cleaned the same day",
    description:
      "Split the savings with a neighbor — $75 each instead of paying separately. Both homes cleaned on the same visit.",
    href: "https://buy.stripe.com/14A9AUaVK5Jc1qX0LA2sM03",
    testId: "stripe-pay-neighbor",
    accent: "bg-[#5FBE1F]",
  },
  {
    id: "gutter-reseal",
    title: "Gutter Reseal Service",
    price: "$195",
    priceNote: "Flat rate",
    description:
      "Cleaning, removal of old sealant, and fresh sealant on every outlet, corner, end cap, and seam. Stops drips at the source.",
    href: "https://buy.stripe.com/6oU8wQ8NC8Vo4D90LA2sM01",
    testId: "stripe-pay-reseal",
    accent: "bg-[#5FBE1F]",
  },
  {
    id: "detached-addon",
    title: "Detached Building Add-On",
    price: "+$40",
    priceNote: "Garage, shop, or shed cleaned the same visit",
    description:
      "Added on top of your home cleaning — easy to handle while the ladder's already out.",
    href: "https://buy.stripe.com/cNicN62pe9ZsedJfGu2sM02",
    testId: "stripe-pay-detached",
    accent: "bg-[#5FBE1F]",
  },
];

export default function Pay() {
  useSeo({
    title: "Pay Online — Glenwood Gutter Guy",
    description:
      "Securely pay for your gutter cleaning, reseal, or detached building add-on online. Powered by Stripe.",
    image: SITE.defaultOg,
    url: `${SITE.url}/pay`,
    type: "website",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="pay-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.24em] text-white/50">
            Pay Online
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
          Pay for your service
        </h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed">
          Once Mike has finished the work, pick the right option below. Stripe
          handles the card capture and emails you a receipt — Glenwood Gutter
          Guy never sees your card number.
        </p>
        <div className="mt-5 flex items-center gap-2 text-xs text-white/50">
          <Lock className="w-4 h-4 text-[#5FBE1F]" />
          <span>Secure checkout · Powered by Stripe</span>
        </div>
      </section>

      {/* Pay options */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <ul className="space-y-4">
          {PAY_OPTIONS.map((opt) => (
            <li key={opt.id}>
              <a
                href={opt.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track("stripe_pay_click", { service: opt.id })
                }
                data-testid={opt.testId}
                className="group block border border-white/10 rounded-sm p-5 sm:p-6 hover:border-[#5FBE1F] hover:bg-white/[0.03] transition-colors"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h2 className="font-display text-2xl sm:text-3xl text-white">
                        {opt.title}
                      </h2>
                      <span className="font-display text-2xl sm:text-3xl text-[#5FBE1F]">
                        {opt.price}
                      </span>
                    </div>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/40">
                      {opt.priceNote}
                    </p>
                    <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed max-w-xl">
                      {opt.description}
                    </p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
                    <div
                      className={`${opt.accent} text-black group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-2 font-bold px-5 py-3 rounded-sm`}
                    >
                      <CreditCard className="w-4 h-4" />
                      Pay now
                    </div>
                  </div>
                </div>
                <div
                  className={`${opt.accent} sm:hidden text-black mt-5 text-center inline-flex w-full items-center justify-center gap-2 font-bold py-3 rounded-sm`}
                >
                  <CreditCard className="w-4 h-4" />
                  Pay now
                </div>
              </a>
            </li>
          ))}
        </ul>

        {/* Trust footer */}
        <div className="mt-12 border-t border-white/10 pt-8 grid sm:grid-cols-3 gap-6 text-sm text-white/60">
          <div className="flex gap-3">
            <ShieldCheck className="w-5 h-5 text-[#5FBE1F] shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-bold mb-1">PCI compliant</div>
              Card details go straight to Stripe — never to our servers.
            </div>
          </div>
          <div className="flex gap-3">
            <CreditCard className="w-5 h-5 text-[#5FBE1F] shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-bold mb-1">All major cards</div>
              Visa, Mastercard, Amex, Discover · Apple Pay & Google Pay on
              supported devices.
            </div>
          </div>
          <div className="flex gap-3">
            <Lock className="w-5 h-5 text-[#5FBE1F] shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-bold mb-1">Instant receipt</div>
              Stripe emails you a receipt the moment the payment clears.
            </div>
          </div>
        </div>

        {/* Need help */}
        <div className="mt-10 text-sm text-white/50">
          Questions or paying a different amount? Just text{" "}
          <a
            href="sms:+17123109514?body=Payment"
            className="text-[#5FBE1F] underline underline-offset-2 hover:text-white"
            data-testid="pay-sms-help"
            onClick={() =>
              track("text_gutters_click", { location: "pay_page_help" })
            }
          >
            (712) 310-9514
          </a>{" "}
          and Mike will sort it out.
        </div>

        {/* SEO + FAQ block */}
        <div className="mt-16 border-t border-white/10 pt-10">
          <h2 className="font-display text-3xl text-white mb-4">
            Pay for Gutter Service Online in Glenwood, IA
          </h2>
          <p className="text-white/70 leading-relaxed mb-4">
            Pay for your Glenwood Gutter Guy job online with a credit or debit
            card, Apple Pay, or Google Pay. Card details are handled by Stripe
            — we never see or store your card number, and you get an instant
            email receipt the moment your payment clears. Most customers pay
            after the work is finished, but you&apos;re welcome to pay anytime
            that fits your schedule.
          </p>
          <p className="text-white/70 leading-relaxed mb-8">
            If your service was a custom quote (repairs, gutter cover install,
            or anything outside the listed flat rates above), text Mike at{" "}
            <a
              href="sms:+17123109514"
              className="text-[#5FBE1F] underline underline-offset-2 hover:text-white"
              data-testid="pay-seo-sms"
            >
              (712) 310-9514
            </a>{" "}
            and he&apos;ll generate a one-off invoice link for the exact amount.
          </p>

          <h3 className="font-display text-2xl text-white mb-4">
            Common questions
          </h3>
          <dl className="space-y-5 text-sm">
            <div>
              <dt className="text-white font-bold mb-1">
                Is paying online safe?
              </dt>
              <dd className="text-white/70 leading-relaxed">
                Yes — every payment runs through Stripe, the same processor
                used by Amazon, Target, and Lyft. Your card details never
                touch our servers; they go straight to Stripe&apos;s
                PCI-DSS-compliant environment.
              </dd>
            </div>
            <div>
              <dt className="text-white font-bold mb-1">
                What if I&apos;m a recurring member?
              </dt>
              <dd className="text-white/70 leading-relaxed">
                If you&apos;re on a{" "}
                <Link
                  to="/plans"
                  className="text-[#5FBE1F] underline underline-offset-2 hover:text-white"
                  data-testid="pay-link-plans"
                >
                  Maintainer, Protector, or Guardian membership
                </Link>
                , you get an automatic member discount on every paid service
                — log in at{" "}
                <Link
                  to="/member"
                  className="text-[#5FBE1F] underline underline-offset-2 hover:text-white"
                  data-testid="pay-link-member"
                >
                  the member portal
                </Link>{" "}
                and the coupon applies at checkout.
              </dd>
            </div>
            <div>
              <dt className="text-white font-bold mb-1">
                Can I get a receipt for taxes or HOA records?
              </dt>
              <dd className="text-white/70 leading-relaxed">
                Stripe emails you a detailed receipt the moment your payment
                clears, with the service description, date, and amount.
                You can also request a printable invoice by texting Mike.
              </dd>
            </div>
            <div>
              <dt className="text-white font-bold mb-1">
                Do you offer Military or First Responder discounts?
              </dt>
              <dd className="text-white/70 leading-relaxed">
                Yes — 15% off for active or retired Military, Police, Fire,
                and EMS. Just mention it when booking and Mike will text you
                a discounted Stripe link.
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
}
